/**
 * MaterialToast — floating "+3 Wood" text that rises from the player
 * character when materials are gathered or loot is picked up.
 *
 * SMA-70 F2. Listens to MSG_GATHER_COMPLETE via the ZoneMessageBus and
 * spawns a text element at the walker's screen position. Each toast rises
 * 40px over 1.5s while fading from alpha 1.0 to 0.0. Multiple toasts
 * stack vertically with a 20px offset per active toast.
 *
 * Pure Phaser — no Colyseus dependency. The scene passes gather events in
 * via `showGatherToast()` or wires the bus subscription externally.
 */

import * as Phaser from 'phaser';
import { ItemType } from '@game/shared';

/** ItemType → display name for toast labels. Falls back to the raw enum
 *  value with underscores replaced by spaces and title-cased. */
const MATERIAL_DISPLAY_NAME: Partial<Record<ItemType, string>> = {
  [ItemType.MaterialWood]: 'Wood',
  [ItemType.MaterialStone]: 'Stone',
  [ItemType.MaterialThatch]: 'Thatch',
  [ItemType.MaterialCloth]: 'Cloth',
  [ItemType.MaterialInk]: 'Ink',
  [ItemType.MaterialWax]: 'Wax',
  [ItemType.MaterialAsh]: 'Ash',
  [ItemType.MaterialThawedWater]: 'Thawed Water',
  [ItemType.MaterialEmber]: 'Ember',
  [ItemType.MaterialSpiritsong]: 'Spiritsong',
};

/** Resolve a display name for an ItemType, with a sensible fallback. */
export function materialDisplayName(itemType: ItemType): string {
  return MATERIAL_DISPLAY_NAME[itemType]
    ?? itemType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const TOAST_RISE_PX = 40;
const TOAST_DURATION_MS = 1500;
const TOAST_STACK_OFFSET = 20;
const TOAST_FONT_FAMILY = "'Almendra', serif";
const TOAST_FONT_SIZE = '16px';
const TOAST_COLOR = '#eae0c8';
const TOAST_DEPTH = 1100;

interface ActiveToast {
  text: Phaser.GameObjects.Text;
  tween: Phaser.Tweens.Tween;
}

export class MaterialToast {
  private scene: Phaser.Scene;
  private activeToasts: ActiveToast[] = [];

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Show a floating material gain toast at the given screen position.
   * @param quantity  Number of items gained (e.g. 3)
   * @param name     Display name of the material (e.g. "Wood")
   * @param screenX  Screen-space X position (walker's position)
   * @param screenY  Screen-space Y position (walker's position)
   */
  show(quantity: number, name: string, screenX: number, screenY: number): void {
    const stackIndex = this.activeToasts.length;
    const startY = screenY - (stackIndex * TOAST_STACK_OFFSET);

    const label = `+${quantity} ${name}`;
    const text = this.scene.add.text(screenX, startY, label, {
      fontFamily: TOAST_FONT_FAMILY,
      fontSize: TOAST_FONT_SIZE,
      color: TOAST_COLOR,
      shadow: {
        offsetX: 1,
        offsetY: 1,
        color: '#000000',
        blur: 3,
        fill: true,
      },
    });
    text.setOrigin(0.5, 1);
    text.setDepth(TOAST_DEPTH);
    text.setScrollFactor(0);
    text.setAlpha(1);

    const toast: ActiveToast = {
      text,
      tween: this.scene.tweens.add({
        targets: text,
        y: startY - TOAST_RISE_PX,
        alpha: { from: 1, to: 0 },
        duration: TOAST_DURATION_MS,
        ease: 'Quad.easeOut',
        onComplete: () => {
          text.destroy();
          const idx = this.activeToasts.indexOf(toast);
          if (idx !== -1) this.activeToasts.splice(idx, 1);
        },
      }),
    };

    this.activeToasts.push(toast);
  }

  destroy(): void {
    for (const toast of this.activeToasts) {
      toast.tween.destroy();
      toast.text.destroy();
    }
    this.activeToasts = [];
  }
}
